import React, { useState } from "react";

import * as api from "./api";
import { Async, Panel } from "./panels";
import { useEndpoint } from "./useOffer";

/**
 * Workstation · Ads & Creative — **Custom Creative Sessions, on their own screen.**
 *
 * WHY THIS IS A SCREEN AND NOT A DROPDOWN
 * =======================================
 * The Founder's ruling, 2026-09-08: *"it must live on its own separate screen,
 * not inline inside an active creative session. When someone is in the middle
 * of a new session, they should not see previous sessions on that screen at
 * all."*
 *
 * `AdsCreative.jsx` enforces the second half by rendering EITHER this screen or
 * the open session, never both — see its `sessionId` branch. This component is
 * therefore never mounted while a session is open, and that is a structural
 * guarantee rather than a `hidden` class.
 *
 * WHAT IT FIXES
 * =============
 * There was no list endpoint at all. Thirteen CCS routes, not one of them a
 * list, so a session navigated away from was gone. Counted against live data on
 * 2026-09-08: **34 of 37 sessions unreachable** — 32 in DEVELOPMENT, 2 in
 * REVIEW. v2's own Custom button was creating sessions v2 could not find again.
 */

const STAGES = [
  { id: "", label: "All" },
  { id: "DEVELOPMENT", label: "In progress" },
  { id: "REVIEW", label: "In review" },
  { id: "SUBMITTED", label: "Submitted" },
];

const STAGE_LABEL = {
  DEVELOPMENT: "In progress",
  REVIEW: "In review",
  SUBMITTED: "Submitted",
};

/** A date a person can read, or a dash. Never a fabricated "now". */
function when(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function SessionRow({ session, onOpen }) {
  const stage = session.stage || "—";
  const routed = session.submission?.routed;
  return (
    <li className="ws-session-row">
      <button
        type="button"
        className="ws-session-item"
        onClick={() => onOpen(session.session_id)}
        data-testid={`ws-custom-session-${session.session_id}`}
      >
        <span className="ws-session-main">
          <span className="ws-session-offer">{session.offer_name || session.offer_id || "—"}</span>
          <span className="dim ws-session-meta">
            {session.draft?.video_format || "no format chosen"}
            {" · updated "}
            {when(session.updated_at)}
          </span>
        </span>
        <span className="ws-session-tags">
          <span className={`ws-tag ws-stage-${String(stage).toLowerCase()}`}>
            {STAGE_LABEL[stage] || stage}
          </span>
          {routed ? <span className="ws-tag">routed</span> : null}
        </span>
      </button>
    </li>
  );
}

export default function CustomSessions({ offerId, onOpen }) {
  // Not defaulted to the selected offer: the whole point of this screen is
  // finding a session you lost, and scoping it to whatever offer happens to be
  // selected would hide most of them again.
  const [stage, setStage] = useState("");
  const [thisOfferOnly, setThisOfferOnly] = useState(false);

  const params = {};
  if (stage) params.stage = stage;
  if (thisOfferOnly && offerId) params.offer_id = offerId;

  const result = useEndpoint(
    () => api.listCustomSessions(params),
    [stage, thisOfferOnly, offerId],
  );

  return (
    <Panel
      title="Custom Creative Sessions"
      subtitle="Every Custom session you have started. Open one to pick it back up."
      dominant
    >
      <div className="ws-session-filters" data-testid="ws-custom-session-filters">
        {STAGES.map((s) => (
          <button
            key={s.id || "all"}
            type="button"
            className={`btn-ghost btn-sm ${stage === s.id ? "is-active" : ""}`}
            onClick={() => setStage(s.id)}
            aria-pressed={stage === s.id}
            data-testid={`ws-session-filter-${s.id || "all"}`}
          >
            {s.label}
          </button>
        ))}
        {offerId ? (
          <label className="ws-session-scope">
            <input
              type="checkbox"
              checked={thisOfferOnly}
              onChange={(e) => setThisOfferOnly(e.target.checked)}
              data-testid="ws-session-this-offer-only"
            />
            <span>This offer only</span>
          </label>
        ) : null}
      </div>

      <Async
        result={result}
        empty="No Custom sessions yet. Start one from Ads &amp; Creative."
      >
        {(data) => {
          const rows = data?.sessions || [];
          if (rows.length === 0) {
            // Three states, not two. This is the endpoint answering with
            // nothing — distinct from the endpoint failing, which `Async`
            // renders, and from never having asked.
            return (
              <div className="state ws-state" data-testid="ws-custom-sessions-empty">
                {stage || thisOfferOnly
                  ? "No sessions match this filter. The endpoint answered; nothing matched."
                  : "No Custom sessions yet. The endpoint answered; you have none."}
              </div>
            );
          }
          return (
            <>
              <ul className="ws-session-list" data-testid="ws-custom-session-list">
                {rows.map((s) => (
                  <SessionRow key={s.session_id} session={s} onOpen={onOpen} />
                ))}
              </ul>
              <p className="dim ws-session-count">
                {rows.length} session{rows.length === 1 ? "" : "s"}
                {/* A list cut at its limit and a list that is genuinely this
                    long look identical without this. The endpoint reports it;
                    hiding it here would waste that. */}
                {data?.truncated
                  ? " shown — there are more than this. Narrow the filter to see the rest."
                  : ""}
              </p>
            </>
          );
        }}
      </Async>
    </Panel>
  );
}
